import React, {
  useContext,
  useEffect,
  useRef,
} from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';
import { useSelector } from 'react-redux';

import { AngularContext } from 'react-app';
import { useAppDispatch } from 'redux/store';
import { clearAiRolePlaySubmissionPendingPointsModal } from 'redux/actions/ai-role-play';
import { RootState } from 'redux/schemas';
import { RolePlayInteractionMode } from 'redux/schemas/models/ai-role-play';

import { Button } from 'react-bootstrap';
import { gray5 } from 'styles/global_defaults/colors';
import prodPathReplace from 'shared/prod-path-rewrite';
import LoadingWrapper from 'shared/components/loading-wrapper';
import moment from 'moment';
import { config } from '../../../../../config/pendo.config.json';
import AiConversationContext,
{ AIRolePlayStatus, VoiceRecordingState } from './context';
import ChatBar from './chat-bar';
import useAiRolePlayHelper from '../hooks/use-ai-role-play-helper';
import Chats from './chats';
import AiFeedback from './ai-feedback';
import { ClosingReason } from './stream-ai-response';
import ScenarioSection from './scenario-section';
import CompletionSection from './completion-section';
import ScrollHandlerButton from './scroll-handler-button';
import VoiceCardsContainer from './voice-mode/voice-cards-container';
import OpeningNarrative from './opening-narrative';
import AudioTranscriptLoadingState from './voice-result/audio-transcript-loading-state';
import ChatTranscriptView from './voice-result/chat-transcript-view';
import VoiceTerminatedStatus from './voice-result/voice-terminated-status';

/**
 * Shared body for both the live conversation modal and the read-only
 * submission view. It renders the conversation column (voice/text branching)
 * and the feedback column, driven entirely by AiConversationContext + the
 * submission record from the store. Each consumer supplies its own outer
 * wrapper and header.
 */
const ConversationBody = () => {
  const { getResponsivePadding, isTabletOrSmaller } = useAiRolePlayHelper();
  const dispatch = useAppDispatch();
  const { injectServices } = useContext(AngularContext);
  const [$uibModal] = injectServices(['$uibModal']);
  const {
    aiRolePlayStatus,
    messages,
    sessionData,
    isRolePlayStarting,
    handleStartConversation,
    voiceRecordingState,
    interactionMode,
    isConversationLoading,
  } = useContext(AiConversationContext);
  const submission = useSelector((state: RootState) => (sessionData.submissionId
    ? state.models.aiRolePlaySubmissions[sessionData.submissionId]
    : null));
  const pendingPointsModal = useSelector((state: RootState) => (sessionData.submissionId
    ? state.app.aiRolePlay.pendingPointsModal[sessionData.submissionId]
    : null));

  const hasFeedbackPane = aiRolePlayStatus === AIRolePlayStatus.COMPLETED
    && submission?.closingReason === ClosingReason.COMPLETED;
  const isBodyPartitioned = !isTabletOrSmaller && hasFeedbackPane;

  // Voice role plays defer the points/skill-tags modal (see
  // use-ai-role-play-submission-pusher-events.ts) until this submission's
  // feedback pane actually opens, so it doesn't interrupt the AI's final
  // spoken statement.
  useEffect(() => {
    if (!hasFeedbackPane || !pendingPointsModal || !$uibModal || !sessionData.submissionId) return;

    const { pointsReceived, leaderboardPoints, leaderboardRank, priorLeaderboardRank, skillTags } = pendingPointsModal;
    const modalInstance = $uibModal.open({
      templateUrl: 'shared/templates/points-modal.html',
      windowClass: 'points-modal',
      controller: 'PointsModalCtrl as vm',
      resolve: {
        pointsReceived,
        leaderboardPoints,
        leaderboardRank,
        priorLeaderboardRank,
        extras: { skillTags },
      },
    });
    modalInstance.result.finally(() => {
      dispatch(clearAiRolePlaySubmissionPendingPointsModal({ aiRolePlaySubmissionId: sessionData.submissionId as number }));
    });
  }, [hasFeedbackPane, pendingPointsModal, $uibModal, dispatch, sessionData.submissionId]);

  const showOpeningNarrative = () => {
    if (interactionMode === RolePlayInteractionMode.VOICE) {
      return (
        voiceRecordingState === VoiceRecordingState.RECORDING
      );
    }
    return (
      aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS
        || aiRolePlayStatus === AIRolePlayStatus.COMPLETED
    );
  };

  const styles = css`
    min-height: 0;
    flex-direction: ${isBodyPartitioned ? 'row' : 'column'};
    overflow-y: ${isBodyPartitioned ? 'hidden' : 'auto'};
    .conversation-container {
      .scenario-toggle-button {
        margin-top: 25px;
      }
      height: ${isBodyPartitioned ? 'auto' : '100%'};
      width: ${isBodyPartitioned ? '50%' : '100%'};
      ${interactionMode === RolePlayInteractionMode.VOICE
        && (aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS
        || submission?.closingReason === ClosingReason.TERMINATED
        || aiRolePlayStatus === AIRolePlayStatus.FAILED)
        && css`
          background-image: url(${prodPathReplace('images/voice-role-play-background.png')});
          background-size: cover;
          background-position: center;
      `}
      .conversation-body-container {
        min-height: 0;
        flex: 1;
        overflow-y: auto;
      }
      .conversation-footer-container {
        flex-shrink: 0;
      }
      .conversation-body-container, .conversation-footer-container {
        > * {
          max-width: 800px;
          width: 100%;
          margin-left: auto;
          margin-right: auto;
        }
        padding-left: ${getResponsivePadding()}px;
        padding-right: ${getResponsivePadding()}px;
      }
    }
    .feedback-container {
      width: ${isBodyPartitioned ? '50%' : '100%'};
      border-left: ${isBodyPartitioned ? 1 : 0}px solid ${gray5};
      overflow-y: ${isBodyPartitioned ? 'auto' : 'unset'};
    }
  `;
  const conversationBodyRef = useRef<HTMLDivElement>(null);
  const feedbackContainerRef = useRef<HTMLDivElement>(null);

  const lastMessageStatus = messages[messages.length - 1]?.status;
  const totalMessages = messages.length;

  // Auto-scroll to bottom when a new message is added or when the last message's status changes (e.g. from pending to sent).
  useEffect(() => {
    const bodyElement = conversationBodyRef.current;
    const timeoutId = setTimeout(() => {
      // Guard in case the element has been detached or does not support scrollTo.
      if (bodyElement && typeof bodyElement.scrollTo === 'function') {
        bodyElement.scrollTo({
          top: bodyElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    });
    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    lastMessageStatus,
    totalMessages,
    submission?.finishedAt,
    isConversationLoading,
  ]);

  // Scroll to feedback container when feedback generation starts
  // Skip when isBodyPartitioned since feedback is already visible in side-by-side layout
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (!isBodyPartitioned
      && aiRolePlayStatus === AIRolePlayStatus.COMPLETED
      && submission?.closingReason === ClosingReason.COMPLETED) {
      timeoutId = setTimeout(() => {
        if (feedbackContainerRef?.current
            && typeof feedbackContainerRef.current.scrollIntoView === 'function') {
          feedbackContainerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      });
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    submission?.feedback,
    isBodyPartitioned,
    aiRolePlayStatus,
    submission?.closingReason,
  ]);

  return (
    <div css={styles} className='body-container h-100 d-flex border-top'>
      <div className='position-relative conversation-container d-flex flex-column'>
        <LoadingWrapper isLoaded={!isConversationLoading} className='flex-column flex-grow-1'>
          <div
            ref={conversationBodyRef}
            className='pt-5 conversation-body-container d-flex flex-column'
          >
            {(aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS
            || submission?.closingReason === ClosingReason.TERMINATED
            || aiRolePlayStatus === AIRolePlayStatus.FAILED)
            && interactionMode === RolePlayInteractionMode.VOICE
            && (
              <VoiceCardsContainer />
            )}
            <ScenarioSection />
            {aiRolePlayStatus === AIRolePlayStatus.NOT_STARTED && (
              <div className='d-flex justify-content-center mt-5'>
                <Button
                  onClick={handleStartConversation}
                  disabled={isRolePlayStarting}
                  data-qa={config.pendo.activities.aiRolePlay.conversationModal.begin}
                >
                  {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.BEGIN()}
                </Button>
              </div>
            )}
            {showOpeningNarrative() && <OpeningNarrative />}
            {interactionMode === RolePlayInteractionMode.VOICE
            && (submission?.closingReason === ClosingReason.TERMINATED
              || aiRolePlayStatus === AIRolePlayStatus.FAILED)
            && (
            <VoiceTerminatedStatus
              isTerminationDueToInactivity={aiRolePlayStatus === AIRolePlayStatus.FAILED}
            />
            )}
            {(
              aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS
              || aiRolePlayStatus === AIRolePlayStatus.COMPLETED
            ) && interactionMode === RolePlayInteractionMode.TEXT
            && <Chats />}
            {aiRolePlayStatus === AIRolePlayStatus.COMPLETED
              && interactionMode === RolePlayInteractionMode.VOICE
              && submission?.closingReason === ClosingReason.COMPLETED
              && (
                <div className='mt-4'>
                  {submission?.mergedPlaybackUrl ? (
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <audio controls src={submission.mergedPlaybackUrl} className='w-100' />
                  ) : (
                    <AudioTranscriptLoadingState showTranscriptRow={messages.length === 0} />
                  )}
                  {messages.length > 0 && (
                    <div className='mt-3'>
                      <ChatTranscriptView messages={messages} />
                    </div>
                  )}
                  {submission?.finishedAt && (
                    <p
                      className='font-weight-normal mb-4'
                    >
                      {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.COMPLETED_ON(moment(submission.finishedAt).format('LLL'))}
                    </p>
                  )}
                </div>
              )}
            {(aiRolePlayStatus === AIRolePlayStatus.COMPLETED
              && interactionMode === RolePlayInteractionMode.TEXT)
              && <CompletionSection />}
          </div>
          <ScrollHandlerButton containerRef={conversationBodyRef} />
        </LoadingWrapper>
        <div className='w-100 conversation-footer-container'>
          {aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS
          && interactionMode === RolePlayInteractionMode.TEXT && (
            <ChatBar />
          )}
        </div>
      </div>
      {hasFeedbackPane && (
        <div ref={feedbackContainerRef} className='feedback-container'>
          <AiFeedback
            submissionId={sessionData.submissionId}
          />
        </div>
      )}
    </div>
  );
};

export default ConversationBody;
